import { Link } from "react-router-dom";

const cardClass =
  "rounded-xl border border-slate-800/80 bg-slate-900/40 px-5 py-5";

const PreviewImage = ({
  src,
  alt,
}: {
  src: string;
  alt: string;
}) => (
  <div className="overflow-hidden rounded-lg border border-slate-800/80 bg-black">
    <img
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      className="block w-full"
    />
  </div>
);

export const HowItWorks = () => {
  return (
    <div className="min-h-screen p-6">
      <div className="mx-auto w-full max-w-2xl">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-slate-500 transition hover:text-slate-300"
        >
          ← Back
        </Link>

        <div className="mt-8">
          <h1 className="text-xl font-medium text-slate-100">How this works</h1>
          <p className="mt-2 text-sm text-slate-500">
            Pickles, Tacos, and Tech
          </p>
        </div>

        <div className="mt-8 space-y-8 text-sm leading-relaxed text-slate-400">
          <section className={cardClass}>
            <h2 className="font-medium text-slate-200">Why this exists</h2>
            <p className="mt-3">
              I've been to too many gatherings where the networking was weak, the
              audience was off, and the topics weren't what I thought. I wanted
              the opposite — high signal, meaningful engagement, and people who
              walk out with solutions and a network that helps them drive
              outcomes.
            </p>
            <p className="mt-3">
              This is my personal experiment in building what I have a hard time
              finding. I've been building networking tools to see how I can push
              past my own Dunbar number — how to drive engagement that's
              meaningful instead of transactional, and how to stretch past some
              of my own social limitations.
            </p>
            <p className="mt-3">
              Pickles, Tacos, and Tech is an experiment in process: ways to give
              community value that founders actually want on the calendar — the
              kind of event you don't take off your schedule.
            </p>
          </section>

          <section className={cardClass}>
            <h2 className="font-medium text-slate-200">
              Patterns that drive high signal
            </h2>
            <p className="mt-3">
              I'm borrowing from a few things that already work — and blending
              them on purpose:
            </p>
            <ul className="mt-3 list-inside list-disc space-y-2 text-slate-400">
              <li>
                <span className="text-slate-300">Tinder-style discovery</span> —
                swipe through people in the room, one card at a time, instead
                of wandering with a drink and hoping for luck.
              </li>
              <li>
                <span className="text-slate-300">Duolingo-style learning</span>{" "}
                — small rounds, steady progress, a reason to come back and
                finish meeting the room.
              </li>
              <li>
                <span className="text-slate-300">Speed dating</span> — time-boxed
                exposure to many people fast, so you find the few worth a real
                conversation.
              </li>
            </ul>
            <p className="mt-4">
              These aren't gimmicks. They're real interaction patterns chosen to
              raise signal-to-noise and drive social partnerships that benefit
              the whole community — not just a stack of transactional
              handshakes.
            </p>
            <div className="mt-5">
              <PreviewImage
                src="/preview/attendee-card.png"
                alt="Example attendee card showing superpower, challenge, and solution"
              />
              <p className="mt-2 text-xs text-slate-500">
                Each card surfaces what someone is great at, what's keeping them
                up, and what they're building — before you ever say hello.
              </p>
            </div>
          </section>

          <section>
            <h2 className="font-medium text-slate-200">When you sign up</h2>
            <p className="mt-2">
              I ask you a bunch of questions — your superpower, what's keeping
              you up at night, what you're building. Those answers don't sit in
              a spreadsheet. They go into my own personal networking engine.
            </p>
            <p className="mt-3">
              I look at every profile and ask: how can I help, who in my network
              can help, or who should you connect with?
            </p>
            <p className="mt-3">
              I'm a super-connector in training — and this event is my gym.
              Every signup and every intro is a rep in getting better at putting
              your business first.
            </p>
          </section>

          <section>
            <h2 className="font-medium text-slate-200">
              Before you walk in the door
            </h2>
            <p className="mt-2">
              I look across my network for people who either have a solution that
              could help you, or whose problem your superpower could solve. Then
              I make LinkedIn introductions and kick off the conversation — so
              when you show up, you're not starting from zero. You already know
              someone in the room you want to talk to.
            </p>
          </section>

          <section className={cardClass}>
            <h2 className="font-medium text-slate-200">
              Why you walk in with an edge
            </h2>
            <p className="mt-3">
              The goal is simple: you come into the room knowing that the
              solution, the next project, the next revenue push, or the next
              marketing goal — someone is already thinking about that problem
              and ready to go.
            </p>
            <div className="mt-5">
              <PreviewImage
                src="/preview/everyone-in-the-room.png"
                alt="Grid of attendee photos from the event room"
              />
              <p className="mt-2 text-xs text-slate-500">
                Tap anyone. Jump to their card. Find the conversation that
                matters.
              </p>
            </div>
          </section>

          <section>
            <h2 className="font-medium text-slate-200">What to expect</h2>
            <p className="mt-2">
              Come in with a warm intro. Walk out with an insight, a potential
              solution, and maybe someone else in the room who gets the same
              problem from a different angle.
            </p>
          </section>
        </div>

        <div className={`mt-10 ${cardClass}`}>
          <p className="text-sm text-slate-400">
            Already registered?{" "}
            <Link to="/" className="text-emerald-400 hover:text-emerald-300">
              Sign in with your email
            </Link>{" "}
            on event day.
          </p>
        </div>
      </div>
    </div>
  );
};
